import { CurrencyOrEmpty } from '@plato/app-shared/components/currency-or-empty';
import { MaskedContent } from '@plato/app-shared/components/masked-content';
import { MarginPercentage } from '@plato/ui-legacy/components/list-views/margin-percentage';
import { PricePerUnitLabel } from '@plato/ui-legacy/components/list-views/price-per-unit-label';
import { DataText } from '@plato/ui-legacy/components/typography/data-text';
import { NumberOrEmpty } from '@plato/ui-legacy/components/typography/number-or-empty';
import { PercentageOrEmpty } from '@plato/ui-legacy/components/typography/percentage-or-empty';
import { QuantityText } from '@plato/ui-legacy/components/typography/quantity-text';
import { TextOrEmpty } from '@plato/ui-legacy/components/typography/text-or-empty';
import SmallSpinner from '@plato/ui-legacy/components/ui/small-spinner';
import { useResolvedLocale } from '@plato/ui-legacy/lib/hooks/locale';
import type { PropsWithChildren } from 'react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { ExtendedSalesOrderHeader } from '../../../server/types/grid-expanded-types.ts';
import { api } from '../../../utils/api.ts';
import { Can } from '../../acl/can.tsx';
import { ProductLinkCell } from '../../list-views/components/product-link-cell.tsx';
import { LineItemStatusBadge } from '../../statuses/components/line-item-status-badge.tsx';
import {
    QuoteOrderTimelineAccordion,
    QuoteOrderTimelineAccordionContent,
    QuoteOrderTimelineAccordionItem,
    QuoteOrderTimelineAccordionTrigger,
} from './quote-order-timeline-accordion.tsx';
import {
    QuoteOrderTimelineTable,
    QuoteOrderTimelineTableBody,
    QuoteOrderTimelineTableCell,
    QuoteOrderTimelineTableHead,
    QuoteOrderTimelineTableHeader,
    QuoteOrderTimelineTableRow,
} from './quote-order-timeline-table.tsx';

import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { SrSalesOrderType } from '@plato/data/starrocks/types';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';

type Props = {
    transaction: ExtendedSalesOrderHeader;
    isLastInList: boolean;
    shouldPreLoadLineItems?: boolean;
} & PropsWithChildren;

function OrderInfoBlock({ children }: PropsWithChildren) {
    return <div className="flex flex-1 flex-col">{children}</div>;
}

function OrderInfoHeader({ children }: PropsWithChildren) {
    return <div className="font-medium leading-6 text-neutral-500 text-sm">{children}</div>;
}

function OrderInfoBody({ children }: PropsWithChildren) {
    return <div className="text-sm font-normal text-neutral-800 ">{children}</div>;
}

export function QuoteOrderTimelineBody({
    transaction,
    shouldPreLoadLineItems = false,
}: Omit<Props, 'isLastInList'>) {
    const { canReadClerkAssignment } = useAppConfig();
    const { t } = useTranslation();
    const locale = useResolvedLocale();
    const sendEvent = useAnalytics();

    const {
        data,
        isPending: isLoadingLineItems,
        isFetched: isFetchedLineItems,
        refetch: refetchLineItems,
    } = api.platoUser.grid.getDetailQuoteOrder.getLineItems.useQuery(
        {
            id: transaction.sales_order_id,
        },
        {
            enabled: false,
        },
    );

    const lineItems = data?.lineItems ?? [];

    function getLineItems() {
        if (!isFetchedLineItems && !data) {
            void refetchLineItems();
        }
    }

    useEffect(() => {
        if (shouldPreLoadLineItems && !isFetchedLineItems) {
            getLineItems();
        }
    }, []);

    return (
        <div className="">
            <QuoteOrderTimelineAccordion
                type="single"
                collapsible
                onValueChange={(value) => {
                    if (value) {
                        sendEvent('customer_detail_quote_order_expanded', {
                            transaction_id: transaction.sales_order_id,
                            transaction_type: transaction.type,
                        });
                    }
                    getLineItems();
                }}
            >
                <QuoteOrderTimelineAccordionItem value="item-1">
                    <QuoteOrderTimelineAccordionTrigger
                        onMouseEnter={() => {
                            getLineItems();
                        }}
                        enableTrigger={!!transaction.line_items_count && transaction.line_items_count > 0}
                    >
                        <div className="flex flex-auto flex-col sm:flex-row gap-2 flex-wrap">
                            <OrderInfoBlock>
                                <OrderInfoHeader>
                                    {transaction.type === SrSalesOrderType.QUOTE
                                        ? t('table_header__quote_type', {
                                              ns: 'quotes-orders-list',
                                          })
                                        : t('table_header__order_type', {
                                              ns: 'quotes-orders-list',
                                          })}
                                </OrderInfoHeader>
                                <OrderInfoBody>
                                    {transaction.document_type === transaction.document_type_name
                                        ? transaction.document_type
                                        : `${transaction.document_type}: ${transaction.document_type_name}`}
                                </OrderInfoBody>
                            </OrderInfoBlock>
                            <OrderInfoBlock>
                                <OrderInfoHeader>
                                    {t('table_header__total', { ns: 'quotes-orders-list' })}
                                </OrderInfoHeader>
                                <OrderInfoBody>
                                    <DataText>
                                        <CurrencyOrEmpty decimals={2}>
                                            {transaction.total_amount_net}
                                        </CurrencyOrEmpty>
                                    </DataText>
                                </OrderInfoBody>
                            </OrderInfoBlock>
                            <Can I="read" a="QuoteOrder" field="total_margin">
                                <OrderInfoBlock>
                                    <OrderInfoHeader>
                                        <MaskedContent>
                                            {t('table_header__margin', {
                                                ns: 'quotes-orders-list',
                                            })}
                                        </MaskedContent>
                                    </OrderInfoHeader>
                                    <OrderInfoBody>
                                        <MaskedContent>
                                            <DataText>
                                                <CurrencyOrEmpty decimals={2}>
                                                    {transaction.total_margin}
                                                </CurrencyOrEmpty>
                                                {!!transaction.total_amount_net && (
                                                    <>
                                                        {' ('}
                                                        <MarginPercentage
                                                            margin={transaction.total_margin}
                                                            revenue={transaction.total_amount_net}
                                                        />
                                                        {')'}
                                                    </>
                                                )}
                                            </DataText>
                                        </MaskedContent>
                                    </OrderInfoBody>
                                </OrderInfoBlock>
                            </Can>
                            <OrderInfoBlock>
                                <OrderInfoHeader>
                                    {t('table_header__lines', { ns: 'quotes-orders-list' })}
                                </OrderInfoHeader>
                                <OrderInfoBody>
                                    <DataText>
                                        <NumberOrEmpty>{transaction.line_items_count}</NumberOrEmpty>
                                    </DataText>
                                </OrderInfoBody>
                            </OrderInfoBlock>
                            {canReadClerkAssignment && (
                                <OrderInfoBlock>
                                    <OrderInfoHeader>
                                        {t('table_header__clerk', {
                                            ns: 'quotes-orders-list',
                                        })}
                                    </OrderInfoHeader>
                                    <OrderInfoBody>{transaction.clerk_id}</OrderInfoBody>
                                </OrderInfoBlock>
                            )}
                            {canReadClerkAssignment && (
                                <OrderInfoBlock>
                                    <OrderInfoHeader>
                                        {t('table_header__po_number', {
                                            ns: 'quotes-orders-list',
                                        })}
                                    </OrderInfoHeader>
                                    <OrderInfoBody>
                                        <TextOrEmpty>{transaction.po_number}</TextOrEmpty>
                                    </OrderInfoBody>
                                </OrderInfoBlock>
                            )}
                        </div>
                    </QuoteOrderTimelineAccordionTrigger>
                    <QuoteOrderTimelineAccordionContent className="border-t border-neutral-100 -mx-4">
                        {!isLoadingLineItems && (
                            <QuoteOrderTimelineTable>
                                <QuoteOrderTimelineTableHeader>
                                    <QuoteOrderTimelineTableRow>
                                        <QuoteOrderTimelineTableHead className="">
                                            {t('table_header__product_name', {
                                                ns: 'quotes-orders-list',
                                            })}
                                        </QuoteOrderTimelineTableHead>
                                        <QuoteOrderTimelineTableHead className="text-right">
                                            {t('table_header__quantity_short', {
                                                ns: 'quotes-orders-list',
                                            })}
                                        </QuoteOrderTimelineTableHead>
                                        <QuoteOrderTimelineTableHead className="text-right">
                                            {t('table_header__price', {
                                                ns: 'quotes-orders-list',
                                            })}
                                        </QuoteOrderTimelineTableHead>
                                        <Can I="read" a="QuoteOrderLineItem" field="discount_percent">
                                            <QuoteOrderTimelineTableHead className="text-right">
                                                {t('table_header__rebate', {
                                                    ns: 'quotes-orders-list',
                                                })}
                                            </QuoteOrderTimelineTableHead>
                                        </Can>
                                        <QuoteOrderTimelineTableHead className="text-right">
                                            {t('table_header__total', {
                                                ns: 'quotes-orders-list',
                                            })}
                                        </QuoteOrderTimelineTableHead>
                                        <QuoteOrderTimelineTableHead className="text-right">
                                            {t('table_header__status', {
                                                ns: 'quotes-orders-list',
                                            })}
                                        </QuoteOrderTimelineTableHead>
                                    </QuoteOrderTimelineTableRow>
                                </QuoteOrderTimelineTableHeader>
                                <QuoteOrderTimelineTableBody>
                                    {lineItems.map((line) => (
                                        <QuoteOrderTimelineTableRow key={line.sales_order_item_id}>
                                            <QuoteOrderTimelineTableCell className="w-[45%]">
                                                <ProductLinkCell
                                                    erpProductId={line.product_variant_id}
                                                    productName={line.product_name}
                                                />
                                            </QuoteOrderTimelineTableCell>
                                            <QuoteOrderTimelineTableCell className="text-right">
                                                <QuantityText
                                                    quantity={line.quantity}
                                                    unit={line.quantity_unit_sales}
                                                />
                                            </QuoteOrderTimelineTableCell>
                                            <QuoteOrderTimelineTableCell className="text-right">
                                                <span>
                                                    <CurrencyOrEmpty decimals={2}>
                                                        {line.sales_price}
                                                    </CurrencyOrEmpty>
                                                    <PricePerUnitLabel
                                                        scaleFactor={line.price_scale_factor}
                                                        units={line.quantity_unit_sales}
                                                        locale={locale}
                                                    />
                                                </span>
                                            </QuoteOrderTimelineTableCell>

                                            <Can I="read" a="QuoteOrderLineItem" field="discount_percent">
                                                <QuoteOrderTimelineTableCell className="text-right">
                                                    {typeof line.discount_percent === 'number' ? (
                                                        <DataText>
                                                            <PercentageOrEmpty>
                                                                {line.discount_percent / 100}
                                                            </PercentageOrEmpty>
                                                        </DataText>
                                                    ) : (
                                                        '-'
                                                    )}
                                                </QuoteOrderTimelineTableCell>
                                            </Can>

                                            <QuoteOrderTimelineTableCell className="text-right">
                                                <DataText>
                                                    <CurrencyOrEmpty decimals={2}>
                                                        {line.line_total}
                                                    </CurrencyOrEmpty>
                                                </DataText>
                                            </QuoteOrderTimelineTableCell>
                                            <QuoteOrderTimelineTableCell className="text-right">
                                                <div className="flex justify-end ">
                                                    <LineItemStatusBadge
                                                        status={line.status}
                                                        type={SrSalesOrderType.QUOTE}
                                                    />
                                                </div>
                                            </QuoteOrderTimelineTableCell>
                                        </QuoteOrderTimelineTableRow>
                                    ))}
                                </QuoteOrderTimelineTableBody>
                            </QuoteOrderTimelineTable>
                        )}
                        {isLoadingLineItems && (
                            <div className="flex h-20 items-center justify-center">
                                <SmallSpinner />
                            </div>
                        )}
                        {!isLoadingLineItems && lineItems.length === 0 && (
                            <div className="flex h-10 items-center  justify-center text-sm font-medium text-neutral-800 ">
                                <span>{t('empty_table__no_results')}</span>
                            </div>
                        )}
                    </QuoteOrderTimelineAccordionContent>
                </QuoteOrderTimelineAccordionItem>
            </QuoteOrderTimelineAccordion>
        </div>
    );
}
